import type { AdBreak, AdCreative } from '../types'

export type AdPhase = 'idle' | 'playing' | 'completed'

export interface ActiveAdState {
  breakId: string
  type: AdBreak['type']
  creative: AdCreative
  indexInBreak: number
  startedAtContentTime: number
}

export interface AdEngineSnapshot {
  phase: AdPhase
  active: ActiveAdState | null
  playedBreakIds: Set<string>
  markers: number[]
}

export class AdEngine {
  private breaks: AdBreak[]
  private playedBreakIds = new Set<string>()
  private active: ActiveAdState | null = null
  private phase: AdPhase = 'idle'
  private prerollDone = false

  constructor(breaks: AdBreak[] = []) {
    this.breaks = breaks
  }

  reset(breaks: AdBreak[]) {
    this.breaks = breaks
    this.playedBreakIds = new Set()
    this.active = null
    this.phase = 'idle'
    this.prerollDone = false
  }

  getMarkers(): number[] {
    return this.breaks
      .filter((b) => b.type === 'midroll' && typeof b.offsetSeconds === 'number')
      .map((b) => b.offsetSeconds as number)
      .sort((a, b) => a - b)
  }

  getSnapshot(): AdEngineSnapshot {
    return {
      phase: this.phase,
      active: this.active,
      playedBreakIds: new Set(this.playedBreakIds),
      markers: this.getMarkers(),
    }
  }

  isPlayingAd(): boolean {
    return this.phase === 'playing' && this.active !== null
  }

  getActive(): ActiveAdState | null {
    return this.active
  }

  /** Called on first user play — returns preroll break if needed */
  requestInitialPlay(): AdBreak | null {
    if (this.prerollDone) return null
    const preroll = this.breaks.find(
      (b) => b.type === 'preroll' && !this.playedBreakIds.has(b.id) && b.ads.length > 0,
    )
    if (!preroll) {
      this.prerollDone = true
      return null
    }
    return preroll
  }

  startBreak(breakItem: AdBreak, contentTime: number): ActiveAdState | null {
    if (!breakItem.ads.length) return null
    this.active = {
      breakId: breakItem.id,
      type: breakItem.type,
      creative: breakItem.ads[0],
      indexInBreak: 0,
      startedAtContentTime: contentTime,
    }
    this.phase = 'playing'
    if (breakItem.type === 'preroll') this.prerollDone = true
    return this.active
  }

  /** Check mid-roll at content time; returns break if should fire */
  checkMidroll(contentTime: number): AdBreak | null {
    if (this.phase === 'playing') return null
    const candidates = this.breaks.filter(
      (b) =>
        b.type === 'midroll' &&
        !this.playedBreakIds.has(b.id) &&
        typeof b.offsetSeconds === 'number' &&
        contentTime >= b.offsetSeconds &&
        b.ads.length > 0,
    )
    if (!candidates.length) return null
    // Fire the earliest unplayed midroll that we've passed
    return candidates.sort((a, b) => (a.offsetSeconds ?? 0) - (b.offsetSeconds ?? 0))[0]
  }

  /** Advance to next ad in break, or complete the break */
  onAdEnded(): { next: ActiveAdState | null; breakCompleted: boolean; resumeContentTime: number } {
    if (!this.active) {
      return { next: null, breakCompleted: true, resumeContentTime: 0 }
    }
    const breakItem = this.breaks.find((b) => b.id === this.active!.breakId)
    const resume = this.active.startedAtContentTime
    if (!breakItem) {
      this.completeActiveBreak()
      return { next: null, breakCompleted: true, resumeContentTime: resume }
    }
    const nextIndex = this.active.indexInBreak + 1
    if (nextIndex < breakItem.ads.length) {
      this.active = {
        ...this.active,
        creative: breakItem.ads[nextIndex],
        indexInBreak: nextIndex,
      }
      return { next: this.active, breakCompleted: false, resumeContentTime: resume }
    }
    this.completeActiveBreak()
    return { next: null, breakCompleted: true, resumeContentTime: resume }
  }

  skipCurrentAd(): { next: ActiveAdState | null; breakCompleted: boolean; resumeContentTime: number } {
    return this.onAdEnded()
  }

  private completeActiveBreak() {
    if (this.active) {
      this.playedBreakIds.add(this.active.breakId)
    }
    this.active = null
    this.phase = 'idle'
  }
}
